// ===== Layer 2: 카테고리 설정 (의존성: base.ts만) =====

import type { CodeType} from 'src/types/base';

// API 함수들 import
import { getItemPopupList } from 'src/apis/itemApi';
import { getCustomerList } from 'src/apis/customerApi';
import { getOrganizationList } from 'src/apis/organizaApi';
import { getEmployeeList } from 'src/apis/employeeApi';
import { getUserBySearch } from 'src/apis/authApi';
import { getMenuModalList, getThirdMenuModalList } from 'src/apis/menuApi';
import { getAfeederCodeList, getBfeederCodeList, getCfeederCodeList, getMinorCodeList } from 'src/apis/commonCodeApi';
import { getWarehouseList, getWarehouseManufactList, } from 'src/apis/warehouseApi';
import { getWorkPlaceList } from 'src/apis/workPlaceApi';
import { getBankacctList } from 'src/apis/bankacctApi';
import { getEquipmentPopupList, getEquipmentPopupList_MetalMask } from 'src/apis/equipmentApi';
import { getInspectPopupList } from 'src/apis/inspectApi';
import { getCompanyList } from 'src/apis/companyApi';
import { getBomChildItemList, getBomParentItemList } from 'src/apis/bomApi';
import { getProductOrderPopupList } from 'src/apis/productOrderApi';
import { getProductResultPopupList } from 'src/apis/productResultApi';
import { getProductWorkPopupList } from 'src/apis/productWorkApi';
import { getLocationPopupList } from 'src/apis/locationApi';
import { getSalOrderPopupList } from 'src/apis/salOrderApi';
import { getTqcInspectSpecPopupList } from 'src/apis/tqcInspectSpecApi';
import { getMoldPopupList } from 'src/apis/moldApi';
import { getWmsOrderInPopupList, getWmsOrderOutPopupList } from 'src/apis/wmsOrderApi';

// 렌더러 설정 인터페이스
export interface RendererConfig {
	type: 'commonCode';
	listKey: string; // commonCodeStore에서 가져올 리스트의 키 (예: 'unitList', 'itemAccountList')
	codeField?: string; // 코드 필드명 (기본값: 'minorCd')
	nameField?: string; // 이름 필드명 (기본값: 'codeNm')
}

// 컬럼 설정 인터페이스
export interface ColumnDefinition {
	headerName: string;
	field: string;
	flex?: number;
	width?: number;
	cellRenderer?: string;
	rendererConfig?: RendererConfig; // 렌더러 설정 (방안 3)
}

// 필터 설정 인터페이스 (@deprecated searchOptions 권장)
export interface FilterDefinition {
	key: string;
	label: string;
	dataKey?: string;
}

// 모달 크기 설정 인터페이스
export interface ModalSizeOptions {
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'half' | 'mmd';
	width?: number;  // px 단위 (기본값: 700)
	// height 제거 - size로 자동 결정
}

// 공통코드 스토어 타입 (순환 참조 방지를 위한 타입 정의)
export type CommonCodeStoreType = {
	unitList?: Array<{ minorCd: string; codeNm: string }>;
	itemAccountList?: Array<{ minorCd: string; codeNm: string }>;
	[key: string]: any;
};

/**
 * 모달 검색 옵션 설정
 * - key: 'search' 인 input이 메인 검색어 역할
 * - 배열 순서대로 화면에 렌더링됨
 * - dropdown의 dataKey는 CommonCodeStoreType의 키를 참조
 */
export interface SearchOption {
	key: string;
	label: string;
	type: 'input' | 'dropdown' | 'dateRange';
	placeholder?: string;
	dataKey?: string;
	defaultValue?: string;
	defaultDateRange?: 'currentMonth';
}

/**
 * SearchSection 코드검색 필드 UI 옵션
 * - SearchCodeModalForm의 wrapperOption과 동일한 형태
 */
export interface SearchFieldUiOptions {
	wrapperStyle?: string;
	titleStyle?: string;
	codeInputStyle?: string;
	nameTagStyle?: string;
}

// 카테고리 설정 인터페이스
export interface CategoryDefinition {
	// 기본 정보
	type: CodeType;
	titles: readonly string[];
	
	// API 설정
	apiFunction: (...args: any[]) => Promise<any>;
	codeField: string | string[];
	nameField?: string;
	getApiParams: (searchText: string, filters: any) => any[];
	transformResponse?: (data: any[]) => any[];
	
	// UI 설정
	/** 클라이언트 필터링 대상 필드명 목록 */
	searchFields: readonly string[];
	columns: readonly ColumnDefinition[] | ((commonCodeStore?: CommonCodeStoreType) => ColumnDefinition[]);

	/**
	 * 모달 검색 옵션
	 * - key: 'search' input이 메인 검색어
	 */
	searchOptions?: readonly SearchOption[];
	/** SearchSection 코드/코드명 필드 UI 프리셋 */
	searchFieldUi?: SearchFieldUiOptions;
	modalSize?: ModalSizeOptions;

	/** 데이터 없을 때 그리드에 표시할 메시지 (미설정 시 "조회된 데이터가 없습니다.") */
	emptyMessage?: string;

	// /** @deprecated searchOptions로 대체 */
	// filterFields?: readonly FilterDefinition[];
	// /** @deprecated searchOptions로 대체 */
	// hasFilters?: boolean;
}

// ===== 모든 카테고리 설정 (한 곳에 집중) =====
export const CATEGORIES: readonly CategoryDefinition[] = [
	{
		type: 'wmsOrderOut',
		titles: ['출고지시', '출고지시번호'] as const,
		apiFunction: getWmsOrderOutPopupList,
		codeField: 'wmsOrderNo',
		nameField: 'salesOrderNo',
		getApiParams: (searchText: string) => [searchText],
		searchFields: ['wmsOrderNo', 'salesOrderNo', 'customCd', 'customNm'] as const,
		searchOptions: [
			{
				key: 'search',
				label: '검색어',
				type: 'input',
				placeholder: '출고지시번호, 수주번호 또는 거래처',
			},
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '출고지시번호', field: 'wmsOrderNo', width: 150 },
			{ headerName: '사업장', field: 'compNm', width: 120 },
			{ headerName: '수주번호', field: 'salesOrderNo', width: 150 },
			{ headerName: '요청일', field: 'orderDt', width: 110 },
			{ headerName: '처리기한', field: 'deadlineDt', width: 110 },
			{ headerName: '거래처코드', field: 'customCd', width: 110 },
			{ headerName: '거래처명', field: 'customNm', width: 180 },
		] as const,
		modalSize: {
			size: 'md',
			width: 1150,
		},
		emptyMessage: '출고 가능한 출고지시가 없습니다.',
	},
	{
		type: 'wmsOrderIn',
		titles: ['입고지시', '입고지시번호'] as const,
		apiFunction: getWmsOrderInPopupList,
		codeField: 'wmsOrderNo',
		nameField: 'purchaseOrderNo',
		getApiParams: (searchText: string) => [searchText],
		searchFields: ['wmsOrderNo', 'purchaseOrderNo', 'customCd', 'customNm'] as const,
		searchOptions: [
			{
				key: 'search',
				label: '검색어',
				type: 'input',
				placeholder: '입고지시번호, 발주번호 또는 거래처',
			},
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '입고지시번호', field: 'wmsOrderNo', width: 150 },
			{ headerName: '사업장', field: 'compNm', width: 120 },
			{ headerName: '발주번호', field: 'purchaseOrderNo', width: 150 },
			{ headerName: '요청일', field: 'orderDt', width: 110 },
			{ headerName: '처리기한', field: 'deadlineDt', width: 110 },
			{ headerName: '거래처코드', field: 'customCd', width: 110 },
			{ headerName: '거래처명', field: 'customNm', width: 180 },
		] as const,
		modalSize: {
			size: 'md',
			width: 1150,
		},
		emptyMessage: '입고 가능한 입고지시가 없습니다.',
	},

	// 품목
	{
		type: 'item',
		titles: ['품목', '품목코드', ] as const,
		
		// API 설정
		apiFunction: getItemPopupList,
		codeField: 'itemCd',
		nameField: 'itemNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.itemAccount || 'all',
			filters?.itemGroup || 'all',
			filters?.brandCd || 'all',
			filters?.customCd || 'all'
		],
		
		// UI 설정
		searchFields: ['itemNm', 'itemCd', 'modelNo', 'itemSpec', 'itemUnit'] as const,
		searchOptions: [
			{ key: 'search',      label: '검색어',   type: 'input',    placeholder: '코드 또는 품명' },
			{ key: 'itemAccount', label: '품목계정', type: 'dropdown', dataKey: 'itemAccountList', defaultValue: 'all' },
			{ key: 'itemGroup',   label: '품목그룹', type: 'dropdown', dataKey: 'itemGroupList',   defaultValue: 'all' },
			{ key: 'brandCd',     label: '브랜드',   type: 'dropdown', dataKey: 'brandCdList',     defaultValue: 'all' },
			{ key: 'customCd',    label: '거래처', type: 'dropdown', dataKey: 'customCdList', defaultValue: 'all' },
		],
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[100px] min-w-[100px]",
			nameTagStyle: "w-[140px] min-w-[140px] text-left",
		},
		columns: (commonCodeStore?: CommonCodeStoreType) => {
			const baseColumns: ColumnDefinition[] = [
				{ headerName: '순번', field: 'rowIndex', width: 60 },
				{ headerName: '코드', field: 'itemCd', width: 170 },
				{ headerName: '품명', field: 'itemNm', width: 250 },
				{ headerName: '규격', field: 'itemSpec', width: 200 },
				{ headerName: '모델명', field: 'modelNo', width: 200},
				{ headerName: '위치', field: 'locationNm', width: 100 },
			];
			
			// 단위 컬럼: 공통코드 스토어에서 단위 리스트가 있으면 추가
			if (commonCodeStore?.unitList && commonCodeStore.unitList.length > 0) {
				baseColumns.push({ 
					headerName: '단위', 
					field: 'itemUnit',
                    width: 60,
					cellRenderer: 'commonCodeRenderer',
					rendererConfig: {
						type: 'commonCode',
						listKey: 'unitList',
						codeField: 'minorCd',
						nameField: 'codeNm'
					}
				});
			} else {
				// 단위 리스트가 없어도 기본 컬럼은 표시
				baseColumns.push({ headerName: '단위', field: 'itemUnit', flex: 1 });
			}
			
			baseColumns.push({ 
				headerName: '품목계정', 
				field: 'itemAccount',
                width: 150,
				cellRenderer: 'commonCodeRenderer',
				rendererConfig: {
					type: 'commonCode',
					listKey: 'itemAccountList',
					codeField: 'minorCd',
					nameField: 'codeNm'
				}
			});
			
			return baseColumns;
		},
		
		// 모달 크기 설정 - 필터가 많아서 넓게
		modalSize: {
			size: 'lg',
			width: 1250
		}
	},

	// Bom
	{
		type: 'bom_childItemcd',
		titles: ['bom_childItemcd', 'BOM_자품목',] as const,
		
		// API 설정
		apiFunction: getBomChildItemList,
		codeField: 'childItemcd',
		nameField: 'itemNm',
		// searchText·filters.search = 자품목(주 검색), childItemcd
		// 모품목은 filters.prntItemcd(extraFilter 등)
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.prntItemcd || ''
		],
		
		// UI 설정
		searchFields: ['childItemcd', 'itemNm', 'itemSpec', 'itemUnit'] as const,
		searchOptions: [
			{ key: 'search', label: '자품목', type: 'input', placeholder: '자품목 코드 또는 품명' },
		],
		columns: () => {
			const baseColumns: ColumnDefinition[] = [
				{ headerName: '순번', field: 'rowIndex', width: 60 },
				{ headerName: '코드', field: 'childItemcd', flex: 1 },
				{ headerName: '품명', field: 'itemNm', flex: 1 },
				{ headerName: '규격', field: 'itemSpec', flex: 1 },
				{ headerName: '단위', field: 'itemUnit', flex: 1 },
			];
			
			return baseColumns;
		},
		
		// 모달 크기 설정 - 필터가 많아서 넓게
		modalSize: {
			size: 'lg',
			width: 850
		},
		emptyMessage: '해당 모품목코드의 자품목 목록이 없습니다.'
	},
	{
		type: 'bom_prntItemcd',
		titles: ['bom_prntItemcd', 'BOM_모품목'] as const,
		
		// API 설정
		apiFunction: getBomParentItemList,
		codeField: 'prntItemcd',
		nameField: 'itemNm',
		getApiParams: (prntItemcd: string) => [prntItemcd],
		
		// UI 설정
		searchFields: ['prntItemcd', 'itemNm', 'itemSpec', 'itemUnit'] as const,
		searchOptions: [
			{ key: 'search', label: '모품목', type: 'input' },
		],
		columns: () => {
			const baseColumns: ColumnDefinition[] = [
				{ headerName: '순번', field: 'rowIndex', width: 60 },
				{ headerName: '코드', field: 'prntItemcd', flex: 1 },
				{ headerName: '품명', field: 'itemNm', flex: 1 },
				{ headerName: '규격', field: 'itemSpec', flex: 1 },
				{ headerName: '단위', field: 'itemUnit', flex: 1 },
			];
			
			return baseColumns;
		},
		
		// 모달 크기 설정 - 필터가 많아서 넓게
		modalSize: {
			size: 'lg',
			width: 850
		}
	},
	
	// 거래처
	{
		type: 'customer',
		titles: ['거래처'] as const,
		
		// API 설정
		apiFunction: getCustomerList,
		codeField: 'customCd',
		nameField: 'customNm',
		getApiParams: (searchText: string, filters: any) => [
			filters?.areaCd || 'all',
			searchText || 'all'
		],
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[140px] min-w-[140px] text-left",
		},
				
		// UI 설정
		searchFields: ['customNm', 'customCd','owner'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '거래처', field: 'customCd', width: 60 },
			{ headerName: '상호', field: 'customNm', width: 120 },
			{ headerName: '대표자', field: 'owner', width: 60 },
			{ headerName: '전화번호', field: 'telNo', width: 120 },
			{ headerName: '주소', field: 'address', width: 500 }
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'md',
			width: 950
		}
	},
	
	// 조직
	{
		type: 'organization',
		titles: ['부서', '조직', '상위부서', '통합팀'] as const,
		
		// API 설정
		apiFunction: getOrganizationList,
		codeField: 'orgCd',
		nameField: 'orgNm',
		getApiParams: (searchText: string, filters: any) => [searchText],
		
		// UI 설정
		searchFields: ['orgNm', 'orgCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '부서코드', field: 'orgCd', width: 100 },
			{ headerName: '부서명', field: 'orgNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 450
		},
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[100px] min-w-[100px] text-left",
		},
	},
	
	// 사원
	{
		type: 'employee',
		titles: ['부서장', '사원번호', '사원', '작업자', '담당자', '발주자', '시험자','등록자','검사자','승인자'] as const,
		
		// API 설정
		apiFunction: getEmployeeList,
		codeField: 'empNo',
		nameField: 'empNm',
		getApiParams: (searchText: string, filters: any) => [searchText],
		
		// UI 설정
		searchFields: ['empNo', 'empNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사원번호', field: 'empNo', width: 100 },
			{ headerName: '사원명', field: 'empNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 340
		},
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[60px] min-w-[60px] text-left",
		},
	},
	
	// 사용자
	{
		type: 'user',
		titles: ['사용자','Login ID'] as const,
		
		// API 설정
		apiFunction: getUserBySearch,
		codeField: 'userId',
		nameField: 'userNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			// filters?.dateRangeFrom || '',
			// filters?.dateRangeTo || '',
		],
		transformResponse: (data: any[]) => data.map(item => ({
			userId: item.userId,
			userNm: item.userNm,
			empNo: item.empNo,
			empNm: item.empNm || '',
		})),
		
		// UI 설정
		searchFields: ['userId', 'userNm', 'empNo', 'empNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
			// { key: 'dateRange', label: '기간', type: 'dateRange', defaultDateRange: 'currentMonth' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사용자ID', field: 'userId', flex: 1 },
			{ headerName: '사용자명', field: 'userNm', flex: 1 },
			{ headerName: '사원번호', field: 'empNo', flex: 1 },
			{ headerName: '직원명', field: 'empNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'sm',
			width: 650
		},
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[60px] min-w-[60px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[80px] min-w-[80px] text-left",
		},
	},
	
	// 메뉴
	{
		type: 'menu',
		titles: ['메뉴', '상위메뉴코드'] as const,
		
		// API 설정
		apiFunction: getMenuModalList,
		codeField: 'menuCd',
		nameField: 'menuNm',
		getApiParams: (searchText: string, filters: any) => [searchText],
		
		// UI 설정
		searchFields: ['menuNm', 'menuCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '메뉴코드', field: 'menuCd', width: 100 },
			{ headerName: '메뉴명', field: 'menuNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 400
		}
	},
	// 프로그램(3차 이상 실제 화면 메뉴)
	{
		type: 'thirdMenu',
		titles: ['프로그램', '프로그램 ID'] as const,

		// API 설정
		apiFunction: getThirdMenuModalList,
		codeField: 'menuCd',
		nameField: 'menuNm',
		getApiParams: (searchText: string, filters: any) => [searchText],

		// UI 설정
		searchFields: ['menuNm', 'menuCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input', placeholder: '프로그램 ID 또는 프로그램명' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '프로그램 ID', field: 'menuCd', width: 130 },
			{ headerName: '프로그램 명', field: 'menuNm', flex: 1 }
		] as const,

		modalSize: {
			size: 'sm',
			width: 400
		},
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[360px] min-w-[360px] ",
			titleStyle: "w-[70px] min-w-[70px] text-left",
			codeInputStyle: "w-[110px] min-w-[110px]",
			nameTagStyle: "w-[170px] min-w-[170px] text-left",
		},
	},
	
	// 메뉴권한
	{
		type: 'menuAuth',
		titles: ['메뉴권한'] as const,
		
		// API 설정
		apiFunction: getMinorCodeList,
		codeField: 'minorCd',
		nameField: 'codeNm',
		getApiParams: (searchText: string, filters: any) => ['AUTGROUP', searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			minorCd: item.minorCd,
			codeNm: item.codeNm,
		})),
		
		// UI 설정
		searchFields: ['codeNm', 'minorCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '권한코드', field: 'minorCd', width: 100 },
			{ headerName: '권한명', field: 'codeNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 400
		}
	},
	// 공정코드
	{
		type: 'workplace',
		titles: ['공정코드'] as const,
		
		// API 설정
		apiFunction: getWorkPlaceList,
		codeField: 'workplaceCd',
		nameField: 'workplaceNm',
		getApiParams: (searchText: string, filters: any) => [searchText],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1, 
			workplaceCd: item.workplaceCd,
			workplaceNm: item.workplaceNm,
		})),
		
		// UI 설정
		searchFields: ['workplaceNm', 'workplaceCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '공정코드', field: 'workplaceCd', width: 100 },
			{ headerName: '공정명', field: 'workplaceNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 600
		}
	},
	// 창고
	{
		type: 'warehouse',
		titles: ['창고코드', '입고창고', '불량창고'] as const,
		
		// API 설정
		apiFunction: getWarehouseList,
		codeField: 'warehouseCd',
		nameField: 'warehouseNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.logicCenter || ''
		],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1, 
			warehouseCd: item.warehouseCd,
			warehouseNm: item.warehouseNm,
		})),
		
		// UI 설정
		searchFields: ['warehouseNm', 'warehouseCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '창고코드', field: 'warehouseCd', width: 100 },
			{ headerName: '창고명', field: 'warehouseNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 330
        }
	},	
	// 창고 - 출고창고
	{
		type: 'warehouse_manufact',
		titles: ['출고창고'] as const,
		
		// API 설정
		apiFunction: getWarehouseManufactList,
		codeField: 'warehouseCd',
		nameField: 'warehouseNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.logicCenter || ''
		],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1, 
			warehouseCd: item.warehouseCd,
			warehouseNm: item.warehouseNm,
		})),
		
		// UI 설정
		searchFields: ['warehouseNm', 'warehouseCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '창고코드', field: 'warehouseCd', width: 100 },
			{ headerName: '창고명', field: 'warehouseNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 330
		}
	},	
	// 계좌
	{
		type: 'bankacct',
		titles: ['계좌코드', '계좌번호'] as const,
		
		// API 설정
		apiFunction: getBankacctList,
		codeField: 'bankAcctCd',
		nameField: 'bankAcctNm',
		getApiParams: (searchText: string, filters: any) => [searchText || ''],
		
		// UI 설정
		searchFields: ['bankAcctNm', 'bankAcctCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '계좌코드', field: 'bankAcctCd', flex: 1 },
			{ headerName: '계좌명', field: 'bankAcctNm', flex: 2 },
			{ headerName: '계좌번호', field: 'bankAcctNo', flex: 2 },
			{ headerName: '은행', field: 'bankNm', flex: 2 },
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'md',
			width: 950
		}
	},
	// 설비
	{
		type: 'equipment',
		titles: ['설비코드','계측기'] as const,
	
		apiFunction: getEquipmentPopupList,
		codeField: 'equipmentCd',
		nameField: 'equipmentNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.equipmentType || '',
		],
	
		searchFields: ['equipmentNm', 'equipmentCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '설비코드', field: 'equipmentCd', width: 100 },
			{ headerName: '설비명', field: 'equipmentNm', width: 240  },
			{ headerName: '설비규격', field: 'equipmentSpec', width: 140  },
			// { headerName: '설비유형', field: 'equipmentType', flex: 1 },
		] as const,
	
		modalSize: {
			size: 'md',
			width: 600
		},
		searchFieldUi: {
			// SearchSection의 품목코드 필드 기본 폭
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[200px] min-w-[200px] text-left",
		},
	},
	// 설비 - 메탈마스크
	{
		type: 'equipment_metalMask',
		titles: ['메탈마스크'] as const,
	
		apiFunction: getEquipmentPopupList_MetalMask,
		codeField: 'equipmentCd',
		nameField: 'equipmentNm',
		getApiParams: (searchText: string, filters: any) => [searchText || filters?.search || ''],
	
		searchFields: ['equipmentNm', 'equipmentCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '설비코드', field: 'equipmentCd', width: 100 },
			{ headerName: '설비명', field: 'equipmentNm', flex: 1 },
			{ headerName: '설비규격', field: 'equipmentSpec', flex: 1 },
			{ headerName: '설비유형', field: 'equipmentType', flex: 1 },
			
		] as const,
	
		modalSize: {
			size: 'sm',
			width: 800
		}
	},
	// 설비 점검 
	{
		type: 'inspect',
		titles: ['설비점검코드'] as const,
		
		// API 설정
		apiFunction: getInspectPopupList,
		codeField: 'inspectCd',
		nameField: 'inspectNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.inspectMethod || 'all',
			filters?.inspectNm || 'all',
			filters?.inspectType || 'all',
			filters?.eqmType || 'all'
		],
		transformResponse: (data: any[]) => data.map(item => ({
			eqmType: item.eqmType,
			inspectCd: item.inspectCd,
			inspectNm: item.inspectNm,
			inspectMethod: item.inspectMethod,
			inspectType: item.inspectType,
			inspectValue: item.inspectValue,
			remark: item.remark,
		})),
		
		// UI 설정
		searchFields: ['inspectNm', 'inspectCd', 'inspectMethod', 'inspectType', 'eqmType'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '설비유형', field: 'eqmType', width: 100  },
			{ headerName: '점검코드', field: 'inspectCd', width: 100 },
			{ headerName: '점검명', field: 'inspectNm', flex: 1 },
			{ headerName: '점검방법', field: 'inspectMethod', flex: 1 },
			{ headerName: '점검유형', field: 'inspectType', flex: 1 },
			{ headerName: '결과유형', field: 'inspectValue', flex: 1 },
			{ headerName: '비고', field: 'remark', flex: 1 },
		] as const,
	},
	// 품목별 검사기준 (TQC5020)
	{
		type: 'tqcInspectSpec',
		titles: ['검사기준', '검사항목코드'] as const,

		apiFunction: getTqcInspectSpecPopupList,
		codeField: 'inspItemCd',
		nameField: 'inspItemNm',
		getApiParams: (searchText: string, filters: Record<string, string | undefined>) => {
			const itemCd = filters?.itemCd && filters.itemCd !== 'all' ? filters.itemCd : '';
			const excludeRegistered = filters?.excludeRegistered === 'true';
			return [searchText, itemCd, excludeRegistered];
		},

		searchFields: ['inspItemCd', 'inspItemNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input', placeholder: '코드 또는 검사기준명' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '그룹', field: 'inspItemGroupNm', width: 100 },
			{ headerName: '코드', field: 'inspItemCd', width: 100 },
			{ headerName: '검사기준명', field: 'inspItemNm', flex: 1 },
			{ headerName: '값1', field: 'specValue1', width: 80 },
			{ headerName: '범위', field: 'specRangeNm', width: 70 },
			{ headerName: '값2', field: 'specValue2', width: 80 },
			{ headerName: '단위', field: 'inspUnit', width: 70 },
			{ headerName: '값유형', field: 'valueTypeNm', width: 80 },
			{ headerName: '검사방법', field: 'inspMethodNm', width: 80 },
		] as const,

		modalSize: { size: 'md', width: 950 },
	},
	// 사업장 (SYS4010_R01)
	{
		type: 'company',
		titles: ['사업장', '사업장명'] as const,
		
		// API 설정
		apiFunction: getCompanyList,
		codeField: 'compCd',
		nameField: 'compNm',
		getApiParams: (searchText: string, filters: any) => [searchText || ''],
		
		// UI 설정
		searchFields: ['compNm', 'compCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '사업장코드', field: 'compCd', width: 100 },
			{ headerName: '사업장명', field: 'compNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 600
		}
	},
	// 작업지시
	{
		type: 'productOrder',
		titles: ['작업지시번호', '생산지시번호'] as const,

		apiFunction: getProductOrderPopupList,
		codeField: 'productOrderNo',
		nameField: 'itemNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.compCd || '',
			filters?.workplaceCd || '',
			filters?.closeTag || '',
		],

		searchFields: ['productOrderNo', 'itemCd', 'itemNm', 'workplaceNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '작업지시번호', field: 'productOrderNo', width: 150 },
			{ headerName: '작업일자', field: 'orderDt', width: 100 },
			{ headerName: '품목코드', field: 'itemCd', width: 120 },
			{ headerName: '품목명', field: 'itemNm', flex: 2 },
			{ headerName: '규격', field: 'itemSpec', flex: 1 },
			{ headerName: '공정', field: 'workplaceNm', width: 120 },
			{ headerName: '지시수량', field: 'orderQty', width: 100 },
			{ headerName: '실적수량', field: 'resultQty', width: 100 },
			{ headerName: '마감', field: 'closeTag', width: 70 },
		] as const,

		modalSize: {
			size: 'md',
			width: 1200,
		},
	},
	// 공정작업
	{
		type: 'productWork',
		titles: ['공정작업'] as const,
		
		// API 설정
		apiFunction: getProductWorkPopupList,
		codeField: 'productWorkNo',
		nameField: 'itemNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.workplaceCd || '',
			filters?.closeTag || 'N'
		],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1,
			productWorkNo: item.productWorkNo,
			itemCd: item.itemCd,
			itemNm: item.itemNm,
			itemSpec: item.itemSpec,
			itemUnit: item.itemUnit,
			workDt: item.workDt,
			workplaceCd: item.workplaceCd,
			workplaceNm: item.workplaceNm,
			orderQty: item.orderQty,
			resultQty: item.resultQty,
			workEmp: item.workEmp,
			workEmpNm: item.workEmpNm,
			closeTag: item.closeTag
		})),
		
		// UI 설정
		searchFields: ['productWorkNo', 'itemCd', 'itemNm', 'workplaceNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '공정작업번호', field: 'productWorkNo', width: 150 },
			{ headerName: '품목코드', field: 'itemCd', width: 120 },
			{ headerName: '품목명', field: 'itemNm', flex: 2 },
			{ headerName: '규격', field: 'itemSpec', flex: 1 },
			{ headerName: '작업일', field: 'workDt', width: 100 },
			{ headerName: '작업장', field: 'workplaceNm', width: 100 },
			{ headerName: '지시수량', field: 'orderQty', width: 90 },
			{ headerName: '실적수량', field: 'resultQty', width: 90 }
		] as const,
		
		// 모달 크기 설정 - 큰 크기
		modalSize: {
			size: 'lg',
			width: 850
		}
	},
	// 생산실적
	{
		type: 'productResult',
		titles: ['생산지시','생산실적']as const,

		apiFunction: getProductResultPopupList,
		codeField: ['productOrderNo', 'productResultNo'] as string[],
		nameField: 'productOrderNo',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			{
				compCd: filters?.compCd || '',
				startDate: filters?.productDateFrom || '',
				endDate: filters?.productDateTo || '',
				orderType: filters?.orderType || '',
				workplaceCd: filters?.workplaceCd || '',
				itemCd: filters?.itemCd || '',
				productEmp: filters?.productEmp || '',
			},
		],

		searchFields: ['productOrderNo', 'itemCd'] as const,
		searchOptions: [
			{
				key: 'search',
				label: '검색어',
				type: 'input',
				placeholder: '생산지시번호 또는 품목코드',
			},
			{
				key: 'productDate',
				label: '생산일자',
				type: 'dateRange',
				defaultDateRange: 'currentMonth',
			},
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '생산일자', field: 'productDt', width: 110 },
			{ headerName: '생산실적번호', field: 'productResultNo', width: 160 },
			{ headerName: '생산지시번호', field: 'productOrderNo', width: 160 },
			{ headerName: '사업장', field: 'compCd', width: 90 },
			{ headerName: '공정', field: 'workplaceCd', width: 100 },
			{ headerName: '품목코드', field: 'itemCd', width: 130 },
			{ headerName: '작업자', field: 'productEmp', width: 90 },
			{ headerName: '지시량', field: 'orderQty', width: 90 },
			{ headerName: '생산량', field: 'productQty', width: 90 },
			{ headerName: '불량수량', field: 'badQty', width: 90 },
			{ headerName: '실적량', field: 'acceptQty', width: 90 },
		] as const,

		modalSize: {
			size: 'md',
			width: 1400,
		},
	},
	// 로케이션
	{
		type: 'location',
		titles: ['로케이션', '보관위치'] as const,
		
		// API 설정
		apiFunction: getLocationPopupList,
		codeField: 'locationCd',
		nameField: 'locationNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText,
			filters?.warehouseCd || '',
			filters?.logicCenter || ''
		],
		transformResponse: (data: any[]) => data.map((item, index) => ({
			rowIndex: index + 1,
			locationCd: item.locationCd,
			locationNm: item.locationNm,
			warehouseCd: item.warehouseCd,
			warehouseNm: item.warehouseNm,
			logicCenter: item.logicCenter,
			logicCenterNm: item.logicCenterNm,
			remark: item.remark
		})),
		
		// UI 설정
		searchFields: ['locationCd', 'locationNm', 'warehouseNm', 'logicCenterNm'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '로케이션코드', field: 'locationCd', width: 120 },
			{ headerName: '로케이션명', field: 'locationNm', flex: 1 },
			{ headerName: '창고코드', field: 'warehouseCd', width: 100 },
			{ headerName: '창고명', field: 'warehouseNm', width: 120 },
			{ headerName: '물류센터', field: 'logicCenter', width: 100 },
			{ headerName: '물류센터명', field: 'logicCenterNm', width: 120 },
			// { headerName: '비고', field: 'remark', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 중간 크기
		modalSize: {
			size: 'lg',
			width: 900
		}
	},
	// Afeeder
	{
		type: 'afeeder',
		titles: ['Afeeder'] as const,
		
		// API 설정
		apiFunction: getAfeederCodeList,
		codeField: 'minorCd',
		nameField: 'codeNm',
		getApiParams: (searchText: string, filters: any) => ['PIC_FEEDER', searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			minorCd: item.minorCd,
			codeNm: item.codeNm,
		})),
		
		// UI 설정
		searchFields: ['codeNm', 'minorCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: 'feeder코드', field: 'minorCd', width: 100 },
			{ headerName: 'feeder명', field: 'codeNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 600
		}
	},
	// Bfeeder
	{
		type: 'bfeeder',
		titles: ['Bfeeder'] as const,
		
		// API 설정
		apiFunction: getBfeederCodeList,
		codeField: 'minorCd',
		nameField: 'codeNm',
		getApiParams: (searchText: string, filters: any) => ['PIC_FEEDER', searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			minorCd: item.minorCd,
			codeNm: item.codeNm,
		})),
		
		// UI 설정
		searchFields: ['codeNm', 'minorCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: 'feeder코드', field: 'minorCd', width: 100 },
			{ headerName: 'feeder명', field: 'codeNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 600
		}
	},
	// Cfeeder
	{
		type: 'cfeeder',
		titles: ['Cfeeder'] as const,
		
		// API 설정
		apiFunction: getCfeederCodeList,
		codeField: 'minorCd',
		nameField: 'codeNm',
		getApiParams: (searchText: string, filters: any) => ['PIC_FEEDER', searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			minorCd: item.minorCd,
			codeNm: item.codeNm,
		})),
		
		// UI 설정
		searchFields: ['codeNm', 'minorCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: 'feeder코드', field: 'minorCd', width: 100 },
			{ headerName: 'feeder명', field: 'codeNm', flex: 1 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'sm',
			width: 600
		}
	},
	// 주문항목
	{
		type: 'salesOrder',
		titles: ['수주번호'] as const,
		
		// API 설정
		apiFunction: getSalOrderPopupList,
		codeField: ['salesOrderNo', 'itemNo'] as string[],
		nameField: 'itemNo',
		//getApiParams: (searchText: string, filters: any) => [
			// 	searchText,
			// 	filters?.itemCd === 'all' ? '' : (filters?.itemCd || ''),
			// ],
		getApiParams: (searchText: string, filters: any) => [searchText],
		transformResponse: (data: any[]) => data.map(item => ({
			salesOrderNo: item.salesOrderNo,
			itemNo: item.itemNo,
			// 서버의 수주일자를 팝업 목록에 전달한다.
			orderDt: item.orderDt,
			customNm: item.customNm,
			itemCd: item.itemCd,
			itemNm: item.itemNm,
			itemSpec: item.itemSpec,
			orderUnit: item.orderUnit,
			basOrderNo: item.salesOrderNo,
			basOrderItemNo: item.itemNo,
			orderQty: item.orderQty,
		})),
		
		// UI 설정
		searchFields: ['salesOrderNo', 'itemNm', 'itemNo'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '수주번호', field: 'salesOrderNo', width: 150 },
			{ headerName: '항목번호', field: 'itemNo', width: 150 },
			// 수주 헤더의 주문일자를 수주일자로 표시한다.
			{ headerName: '수주일자', field: 'orderDt', width: 110 },
			{ headerName: '거래처', field: 'customNm', width: 150 },
			{ headerName: '품목코드', field: 'itemCd', width: 150 },
			{ headerName: '품목명', field: 'itemNm', width: 150 },
			{ headerName: '규격', field: 'itemSpec', width: 150 },
			{ headerName: '단위', field: 'orderUnit', width: 100 },
			{ headerName: '수량', field: 'orderQty', width: 80 }
		] as const,
		
		// 모달 크기 설정 - 간단한 구조로 작게
		modalSize: {
			size: 'md',
			width: 1200
		}
	},

	// 금형
	{
		type: 'mold',
		titles: ['금형코드'] as const,

		// API 설정
		apiFunction: getMoldPopupList,
		codeField: 'moldCd',
		nameField: 'moldNm',
		getApiParams: (searchText: string, filters: any) => [
			searchText || '',
			filters?.moldType || ''
		],
		searchFieldUi: {
			wrapperStyle: "w-[300px] min-w-[300px] ",
			titleStyle: "w-[45px] min-w-[45px] text-left",
			codeInputStyle: "w-[80px] min-w-[80px]",
			nameTagStyle: "w-[140px] min-w-[140px] text-left",
		},

		// UI 설정
		searchFields: ['moldNm', 'moldCd'] as const,
		searchOptions: [
			{ key: 'search', label: '검색어', type: 'input' },
		],
		columns: [
			{ headerName: '순번', field: 'rowIndex', width: 60 },
			{ headerName: '금형코드', field: 'moldCd', width: 120 },
			{ headerName: '금형명', field: 'moldNm', width: 170 },
            { headerName:  '금형구분', field: 'moldTypeNm', width: 120},
			{ headerName: '생산품목', field: 'itemCd', width: 150 },
            { headerName: '생산품명', field: 'itemNm', width: 170 },
			{ headerName: '제작사', field: 'makerNm', width: 120 },
		] as const,

		// 모달 크기 설정
		modalSize: {
			size: 'md',
			width: 950
		}
	},
] as const;

// ===== 타입 안전한 조회 함수들 =====

/**
 * 카테고리 타입으로 설정 조회 (O(1) 최적화)
 */
export const getCategoryByType = (type: CodeType): CategoryDefinition | undefined => {
	// 캐시된 매핑 테이블 사용 (O(1) 조회)
	const CATEGORY_MAP = getCategoryMap();
	return CATEGORY_MAP.get(type);
};

/**
 * 카테고리 매핑 테이블 (지연 생성)
 */
let categoryMapCache: Map<CodeType, CategoryDefinition> | null = null;
const getCategoryMap = (): Map<CodeType, CategoryDefinition> => {
	if (!categoryMapCache) {
		categoryMapCache = new Map();
		CATEGORIES.forEach(category => {
			categoryMapCache!.set(category.type, category);
		});
	}
	return categoryMapCache;
};

/**
 * 카테고리 제목으로 설정 조회
 */
export const getCategoryByTitle = (title: string): CategoryDefinition | undefined => {
	return CATEGORIES.find(category => category.titles.includes(title as any));
};
